import { useState, useEffect, useCallback } from 'react'
import type { Organization } from '@alpha-edge/shared'
import { api } from '../../services/api'
import { PageHeader, Alert, FormField, btnPrimary, btnSecondary, card, colors, input } from '../../components/ui'

interface OrgSettings {
  defaultTestTypes: string
  resultDeliveryMethod: string
  autoReleaseResults: boolean
  requireVerification: boolean
  billingPrefix: string
  customFields: string
  // §7.5.1: blank string = use server default (2555 days / ~7y CLIA floor);
  // kept as a string so the input can render empty without fighting a
  // number-coerced 0.
  pdfRetentionDays: string
  // §7.5.2: org branding. logoPath is the storage-adapter-relative path
  // returned by /api/files/upload; themeColor is a #rrggbb hex. Empty
  // strings mean "unset — use defaults".
  brandingLogoPath: string
  brandingThemeColor: string
  // §7.6.5: archive retention. archiveRetentionDays uses same blank-string
  // convention as pdfRetentionDays. legalHoldDefault is a boolean.
  archiveRetentionDays: string
  legalHoldDefault: boolean
  requireTwoPersonRelease: boolean
}

function settingsFromOrg(org: Organization): OrgSettings {
  const s = org.settings ?? {}
  const branding = ((s.branding as Record<string, unknown>) ?? {}) as {
    logoPath?: string
    themeColor?: string
  }
  return {
    defaultTestTypes: Array.isArray(s.defaultTestTypes) ? (s.defaultTestTypes as string[]).join(', ') : '',
    resultDeliveryMethod: (s.resultDeliveryMethod as string) ?? 'hl7',
    autoReleaseResults: (s.autoReleaseResults as boolean) ?? false,
    requireVerification: (s.requireVerification as boolean) ?? true,
    billingPrefix: (s.billingPrefix as string) ?? '',
    customFields: s.customFields ? JSON.stringify(s.customFields, null, 2) : '',
    pdfRetentionDays: typeof s.pdf_retention_days === 'number' ? String(s.pdf_retention_days) : '',
    brandingLogoPath: branding.logoPath ?? '',
    brandingThemeColor: branding.themeColor ?? '',
    archiveRetentionDays: typeof s.archive_retention_days === 'number' ? String(s.archive_retention_days) : '',
    legalHoldDefault: (s.legal_hold_default as boolean) ?? false,
    requireTwoPersonRelease: (s.require_two_person_release as boolean) ?? true,
  }
}

function settingsToPayload(settings: OrgSettings): Record<string, unknown> {
  const pdfDays = settings.pdfRetentionDays.trim()
  const brandingLogoPath = settings.brandingLogoPath.trim()
  const brandingThemeColor = settings.brandingThemeColor.trim()
  const branding: Record<string, unknown> = {}
  if (brandingLogoPath) branding.logoPath = brandingLogoPath
  if (brandingThemeColor) branding.themeColor = brandingThemeColor
  return {
    defaultTestTypes: settings.defaultTestTypes
      ? settings.defaultTestTypes
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
      : [],
    resultDeliveryMethod: settings.resultDeliveryMethod,
    autoReleaseResults: settings.autoReleaseResults,
    requireVerification: settings.requireVerification,
    billingPrefix: settings.billingPrefix || undefined,
    customFields: settings.customFields ? JSON.parse(settings.customFields) : undefined,
    pdf_retention_days: pdfDays === '' ? undefined : Number.parseInt(pdfDays, 10),
    branding: Object.keys(branding).length > 0 ? branding : undefined,
    archive_retention_days:
      settings.archiveRetentionDays.trim() === ''
        ? undefined
        : Number.parseInt(settings.archiveRetentionDays.trim(), 10),
    legal_hold_default: settings.legalHoldDefault,
    require_two_person_release: settings.requireTwoPersonRelease,
  }
}

export function OrgSettingsPage() {
  const [orgs, setOrgs] = useState<readonly Organization[]>([])
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null)
  const [settings, setSettings] = useState<OrgSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const fetchOrgs = useCallback(async () => {
    setLoading(true)
    try {
      const result = await api.get<readonly Organization[]>('/organizations')
      setOrgs(result.data ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organizations')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchOrgs()
  }, [fetchOrgs])

  const handleSelectOrg = (org: Organization) => {
    setSelectedOrg(org)
    setSettings(settingsFromOrg(org))
    setError(null)
    setSuccess(null)
  }

  const updateSetting = <K extends keyof OrgSettings>(key: K, value: OrgSettings[K]) => {
    if (!settings) return
    setSettings({ ...settings, [key]: value })
  }

  const handleSave = async () => {
    if (!selectedOrg || !settings) return
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const payload = settingsToPayload(settings)
      await api.put(`/organizations/${selectedOrg.id}/settings`, payload)
      setSuccess('Settings saved successfully')
      const result = await api.get<Organization>(`/organizations/${selectedOrg.id}`)
      if (result.data) {
        setSelectedOrg(result.data)
        setSettings(settingsFromOrg(result.data))
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div style={{ color: colors.textMuted }}>Loading...</div>

  return (
    <div>
      <PageHeader title="Organization Settings" subtitle="Configure per-client preferences" />
      {error && <Alert type="error" message={error} />}
      {success && <Alert type="success" message={success} />}

      <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr', gap: '1rem' }}>
        <div style={card}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: colors.text }}>Organizations</h3>
          {orgs.length === 0 ? (
            <p style={{ color: colors.textMuted, fontSize: '0.875rem' }}>No organizations found.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {orgs.map((org) => (
                <button
                  key={org.id}
                  onClick={() => handleSelectOrg(org)}
                  style={{
                    ...btnSecondary,
                    textAlign: 'left',
                    background: selectedOrg?.id === org.id ? colors.primary : colors.bgMuted,
                    color: selectedOrg?.id === org.id ? '#fff' : colors.text,
                  }}
                >
                  {org.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {selectedOrg && settings ? (
          <div style={card}>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}
            >
              <h3 style={{ margin: 0, fontSize: '1rem', color: colors.text }}>{selectedOrg.name} Settings</h3>
              <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <FormField
                  name="defaultTestTypes"
                  label="Default Test Types (comma-separated)"
                  value={settings.defaultTestTypes}
                  onChange={(v) => updateSetting('defaultTestTypes', v)}
                  placeholder="e.g. COVID-AG, FLU-A, FLU-B"
                />
              </div>

              <FormField name="resultDeliveryMethod" label="Result Delivery Method">
                <select
                  style={input}
                  value={settings.resultDeliveryMethod}
                  onChange={(e) => updateSetting('resultDeliveryMethod', e.target.value)}
                >
                  <option value="hl7">HL7</option>
                  <option value="fax">Fax</option>
                  <option value="portal">Portal Only</option>
                  <option value="email">Email</option>
                </select>
              </FormField>

              <FormField
                name="billingPrefix"
                label="Billing Prefix"
                value={settings.billingPrefix}
                onChange={(v) => updateSetting('billingPrefix', v)}
                placeholder="e.g. ORG-001"
              />

              <FormField
                name="pdfRetentionDays"
                label="PDF Retention (days)"
                value={settings.pdfRetentionDays}
                onChange={(v) => updateSetting('pdfRetentionDays', v)}
                placeholder="2555 (default, ~7y CLIA floor)"
              />

              <FormField
                name="archiveRetentionDays"
                label="Archive retention (days)"
                value={settings.archiveRetentionDays}
                onChange={(v) => updateSetting('archiveRetentionDays', v)}
                placeholder="1095 (default, ~3y)"
              />

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  id="legalHoldDefault"
                  checked={settings.legalHoldDefault}
                  onChange={(e) => updateSetting('legalHoldDefault', e.target.checked)}
                />
                <label htmlFor="legalHoldDefault" style={{ fontSize: '0.875rem', color: colors.text }}>
                  Apply legal hold by default on new archives
                </label>
              </div>

              <div style={{ gridColumn: '1 / -1', marginTop: '0.5rem' }}>
                <fieldset
                  style={{
                    border: `1px solid ${colors.border}`,
                    borderRadius: '4px',
                    padding: '0.75rem 1rem',
                    margin: 0,
                  }}
                >
                  <legend style={{ fontSize: '0.875rem', color: colors.text, padding: '0 0.25rem' }}>Branding</legend>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <FormField name="brandingThemeColor" label="Theme color">
                      <input
                        type="color"
                        id="brandingThemeColor"
                        value={settings.brandingThemeColor || '#0074d4'}
                        onChange={(e) => updateSetting('brandingThemeColor', e.target.value)}
                        style={{ ...input, height: '36px', padding: '2px', cursor: 'pointer' }}
                      />
                    </FormField>
                    <FormField name="brandingLogoFile" label="Organization logo (max 2 MB)">
                      <input
                        type="file"
                        id="brandingLogoFile"
                        accept="image/png,image/jpeg,image/svg+xml"
                        onChange={async (e) => {
                          const file = e.target.files?.[0]
                          if (!file) return
                          if (file.size > 2 * 1024 * 1024) {
                            setError('Logo must be 2 MB or smaller')
                            return
                          }
                          const form = new FormData()
                          form.append('file', file)
                          try {
                            const res = await api.postForm<{ path: string }>('/files/upload', form)
                            if (res.data?.path) updateSetting('brandingLogoPath', res.data.path)
                          } catch (err) {
                            setError(err instanceof Error ? err.message : 'Logo upload failed')
                          }
                        }}
                      />
                    </FormField>
                  </div>
                  {settings.brandingLogoPath && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: colors.textMuted }}>
                      Current logo path: <code>{settings.brandingLogoPath}</code>
                      <button
                        type="button"
                        style={{ ...btnSecondary, marginLeft: '0.75rem', fontSize: '0.8125rem' }}
                        onClick={() => updateSetting('brandingLogoPath', '')}
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </fieldset>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  id="autoRelease"
                  checked={settings.autoReleaseResults}
                  onChange={(e) => updateSetting('autoReleaseResults', e.target.checked)}
                />
                <label htmlFor="autoRelease" style={{ fontSize: '0.875rem', color: colors.text }}>
                  Auto-release verified results
                </label>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  id="requireVerification"
                  checked={settings.requireVerification}
                  onChange={(e) => updateSetting('requireVerification', e.target.checked)}
                />
                <label htmlFor="requireVerification" style={{ fontSize: '0.875rem', color: colors.text }}>
                  Require verification before release
                </label>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  id="requireTwoPersonRelease"
                  checked={settings.requireTwoPersonRelease}
                  onChange={(e) => updateSetting('requireTwoPersonRelease', e.target.checked)}
                />
                <label htmlFor="requireTwoPersonRelease" style={{ fontSize: '0.875rem', color: colors.text }}>
                  Require a different user to release than the one who verified (two-person QA rule)
                </label>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <FormField
                  name="customFields"
                  label="Custom Fields (JSON)"
                  multiline
                  rows={5}
                  value={settings.customFields}
                  onChange={(v) => updateSetting('customFields', v)}
                  placeholder='{"fieldName": "defaultValue"}'
                />
              </div>
            </div>
          </div>
        ) : (
          <div
            style={{
              ...card,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: colors.textMuted,
            }}
          >
            Select an organization to configure settings.
          </div>
        )}
      </div>
    </div>
  )
}
